import {
  getBackoffDelay,
  isRealtimeConfigured,
  resolveRealtimeWsUrl,
} from "./wsUtils";

function toIso(value) {
  if (!value) return new Date().toISOString();
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return new Date().toISOString();
  return date.toISOString();
}

function normalizeRealtimeMessage(msg = {}) {
  if (!msg || typeof msg !== "object") return null;

  if (msg.type === "auction:event") {
    const auctionId = String(msg.auctionId || msg.roomId || "").trim();
    if (!auctionId) return null;
    const txHash = String(msg.tx_hash || msg.txHash || "")
      .trim()
      .toLowerCase();
    const highestBidder = String(
      msg.highest_bidder || msg.highestBidder || msg.winner || "",
    )
      .trim()
      .toLowerCase();
    const recipient = String(msg.recipient || highestBidder)
      .trim()
      .toLowerCase();
    const quoteAddress = String(msg.quote_address || msg.quoteAddress || "")
      .trim()
      .toLowerCase();
    const blockNumber = msg.block_number ?? msg.blockNumber ?? null;
    return {
      type: "auction:event",
      roomId: auctionId,
      auctionId,
      event: String(msg.event || "").trim(),
      nftAddress: String(msg.nftAddress || "")
        .trim()
        .toLowerCase(),
      tokenId: msg.tokenId != null ? String(msg.tokenId).trim() : "",
      creator: String(msg.creator || "")
        .trim()
        .toLowerCase(),
      highestBidder,
      highest_bidder: highestBidder,
      recipient,
      amount: msg.amount ?? null,
      quoteAddress,
      quote_address: quoteAddress,
      txHash,
      tx_hash: txHash,
      blockNumber,
      block_number: blockNumber,
      created_at: toIso(msg.created_at),
    };
  }

  if (msg.type === "auction:listing") {
    const auctionId = String(msg.auctionId || "").trim();
    if (!auctionId) return null;
    const txHash = String(msg.txHash || msg.tx_hash || "")
      .trim()
      .toLowerCase();
    return {
      type: "auction:listing",
      action: String(msg.action || "").trim(),
      auctionId,
      txHash,
      tx_hash: txHash,
      status: String(msg.status || "").trim(),
      creator: String(msg.creator || "")
        .trim()
        .toLowerCase(),
      winner: String(msg.winner || "")
        .trim()
        .toLowerCase(),
      amount: msg.amount ?? null,
      updated_at: toIso(msg.updated_at),
    };
  }

  if (msg.type === "nft:listing") {
    const nftAddress = String(msg.nftAddress || "")
      .trim()
      .toLowerCase();
    const tokenId = msg.tokenId != null ? String(msg.tokenId).trim() : "";
    if (!nftAddress || !tokenId) return null;
    const txHash = String(msg.txHash || msg.tx_hash || "")
      .trim()
      .toLowerCase();
    return {
      type: "nft:listing",
      action: String(msg.action || "").trim(),
      nftAddress,
      tokenId,
      seller: String(msg.seller || "")
        .trim()
        .toLowerCase(),
      buyer: String(msg.buyer || "")
        .trim()
        .toLowerCase(),
      txHash,
      tx_hash: txHash,
      price: msg.price ?? null,
      created_at: toIso(msg.created_at),
    };
  }

  if (msg.type === "ws:connected") {
    return {
      type: "ws:connected",
      walletAddress: String(msg.walletAddress || "")
        .trim()
        .toLowerCase(),
      connectedAt: toIso(msg.connectedAt),
    };
  }

  return null;
}

function roomName(auctionId) {
  const roomId = String(auctionId || "").trim();
  return roomId ? `auction:${roomId}` : "";
}

function isUpgradeRestartClose(event) {
  const code = Number(event?.code || 0);
  const reason = String(event?.reason || "").toLowerCase();
  return (
    code === 1012 ||
    code === 1013 ||
    reason.includes("upgraded") ||
    reason.includes("new version") ||
    reason.includes("restart")
  );
}

export class WsConnectionManager {
  constructor(walletAddress, onMessage) {
    this.walletAddress = walletAddress;
    this.onMessage = onMessage;
    this.ws = null;
    this.closed = false;
    this.joinedRoomId = null;
    this.retryCount = 0;
    this.reconnectTimer = null;
  }

  connect() {
    if (this.closed) return;
    if (!isRealtimeConfigured()) return;
    this._openSocket();
  }

  joinRoom(auctionId) {
    if (!auctionId) return;
    const nextRoomId = String(auctionId).trim();
    if (!nextRoomId) return;

    const prevRoomId = this.joinedRoomId;
    this.joinedRoomId = nextRoomId;

    if (!this._isSocketOpen()) return;
    if (prevRoomId && prevRoomId !== nextRoomId) {
      this._send({ type: "unsubscribe", room: roomName(prevRoomId) });
    }
    this._send({ type: "subscribe", room: roomName(nextRoomId) });
  }

  leaveRoom() {
    if (!this.joinedRoomId) return;
    const prevRoomId = this.joinedRoomId;
    this.joinedRoomId = null;
    if (this._isSocketOpen()) {
      this._send({ type: "unsubscribe", room: roomName(prevRoomId) });
    }
  }

  close() {
    this.closed = true;
    this._clearReconnectTimer();

    if (this.ws) {
      try {
        this.ws.close();
      } catch {
        // no-op
      }
    }
    this.ws = null;
    this.joinedRoomId = null;
  }

  _openSocket() {
    this._clearReconnectTimer();
    const wsUrl = resolveRealtimeWsUrl();
    if (!wsUrl) return;

    try {
      this.ws = new WebSocket(wsUrl);
    } catch {
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      this.retryCount = 0;
      window.console.info("[ws] connected", {
        url: wsUrl,
        walletAddress: this.walletAddress || "",
        room: this.joinedRoomId ? roomName(this.joinedRoomId) : "global",
        connectedAt: new Date().toISOString(),
      });
      this._send({ type: "subscribe", room: "global" });
      if (this.joinedRoomId) {
        this._send({ type: "subscribe", room: roomName(this.joinedRoomId) });
      }
      this.onMessage?.({
        type: "ws:connected",
        walletAddress: this.walletAddress,
        connectedAt: new Date().toISOString(),
      });
    };

    this.ws.onmessage = (event) => {
      if (!event?.data) return;

      let parsed = null;
      try {
        parsed = JSON.parse(event.data);
      } catch {
        return;
      }

      const msg = normalizeRealtimeMessage(parsed);
      if (!msg) return;
      this.onMessage?.(msg);

      if (msg.type === "auction:listing" && msg.action === "settled") {
        this.onMessage?.({
          type: "auction:event",
          roomId: String(msg.auctionId || ""),
          auctionId: String(msg.auctionId || ""),
          event: "settled",
          winner: msg.winner || "",
          highestBidder: msg.winner || "",
          highest_bidder: msg.winner || "",
          amount: msg.amount ?? null,
          txHash: msg.txHash || "",
          tx_hash: msg.tx_hash || "",
          block_number: msg.blockNumber ?? null,
          created_at: msg.updated_at || new Date().toISOString(),
        });
      }
    };

    this.ws.onclose = (event) => {
      const closeCode = Number(event?.code || 0);
      const closeReason = String(event?.reason || "");
      window.console.warn("[ws] closed", {
        url: wsUrl,
        code: closeCode,
        reason: closeReason,
        retryCount: this.retryCount,
      });
      this.ws = null;
      if (this.closed) return;
      if (isUpgradeRestartClose(event)) {
        this._scheduleFastReconnect();
        return;
      }
      this._scheduleReconnect();
    };

    this.ws.onerror = () => {
      window.console.warn("[ws] error", { url: wsUrl });
      if (!this.ws) return;
      try {
        this.ws.close();
      } catch {
        // no-op
      }
    };
  }

  _scheduleReconnect() {
    this._scheduleReconnectWithDelay(getBackoffDelay(this.retryCount));
    this.retryCount += 1;
  }

  _scheduleFastReconnect() {
    // Service restarts are transient; reconnect quickly.
    this.retryCount = 0;
    this._scheduleReconnectWithDelay(300);
  }

  _scheduleReconnectWithDelay(delayMs) {
    if (this.closed) return;
    this._clearReconnectTimer();
    this.reconnectTimer = window.setTimeout(() => {
      this._openSocket();
    }, Math.max(50, Number(delayMs) || 0));
  }

  _clearReconnectTimer() {
    if (!this.reconnectTimer) return;
    window.clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  _isSocketOpen() {
    return Boolean(this.ws && this.ws.readyState === WebSocket.OPEN);
  }

  _send(payload) {
    if (!this._isSocketOpen()) return;
    try {
      this.ws.send(JSON.stringify(payload));
    } catch {
      // no-op
    }
  }
}
